import { FontAwesome, Ionicons, AntDesign } from "@expo/vector-icons";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { View, Text, Pressable } from "react-native";
import CountryFlag from "react-native-country-flag";

import BeneficiaryModal from "@/components/modal/banking/BeneficiaryModal";
import { Button, ButtonText } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Menu, MenuItem, MenuItemLabel } from "@/components/ui/menu";
import {
  Modal,
  ModalBackdrop,
  ModalCloseButton,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { checkIcon } from "@/utils/transactions/ExchangeCurrencies";

interface CardContactProps {
  id: string;
  nickname: string;
  country: string;
  institutionName: string;
  accountnumberiban?: string;
  routingnumberswiftbic?: string;
  rail?: string;
  swiftbic?: string;
  status?: string;
  data?: any;
  handlePress: () => void;
  onAction?: (action: string) => void;
}

export default function CardContact(props: CardContactProps) {
  const [visible, setVisible] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { t } = useTranslation();

  const validateStatus = {
    ACTIVE: t("common:status.active"),
    PENDING_L2F: t("common:status.pending_l2f"),
  };

  const handlePress = () => {
    props.handlePress();
  };

  const handleActionDelete = () => {
    setShowDeleteModal(false);
    setVisible(false);
    props.onAction(props.id);
  };

  return (
    <>
      <Pressable
        className="transition duration-200 bg-white1 flex-row rounded-lg p-4 m-1 h-24 w-96 shadow hover:shadow-md"
        onPress={handlePress}
      >
        <View className="flex-row flex-grow items-center w-full">
          <View className="w-1/5 flex-col items-start">
            <View className="items-center gap-2">
              {props.rail !== "CRYPTO" ? (
                <CountryFlag
                  isoCode={props.country.toLowerCase()}
                  size={28}
                  style={{ borderRadius: 4 }}
                />
              ) : (
                <>{checkIcon(props.data?.walletInformation?.assetTypeId, 40)}</>
              )}
            </View>
          </View>
          <View className="flex-row w-3/5 p-4 items-center">
            <View className="flex-col items-center w-full gap-1">
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                className="text-base text-center text-black1 font-bold"
              >
                {props.nickname}
              </Text>
              <View className="flex-row items-center">
                <Text className="text-xs text-center text-black1">
                  {props.rail}
                </Text>
              </View>
              <View className="flex-row items-center">
                <Text
                  numberOfLines={1}
                  className="text-xs text-center font-light"
                >
                  {props.rail !== "CRYPTO"
                    ? (props.institutionName ?? "")
                    : (props.data.walletInformation?.assetTypeId?.split(
                        "_"
                      )[0] ?? "")}
                </Text>
              </View>
            </View>
          </View>
          <View className="flex-row items-center justify-center w-1/5">
            <FontAwesome
              name="circle"
              size={8}
              color={props.status === "ACTIVE" ? "#009B00" : "#E1DE00"}
            />
            <Text
              className={`text-xs text-center mx-1 ${
                props.status === "ACTIVE" ? "text-green" : "text-yellowdarker"
              }`}
            >
              {validateStatus[props.status]?.toUpperCase() ?? props.status}
            </Text>
          </View>
        </View>
        <View className="absolute z-50 p-1 top-0 right-0 h-full justify-center">
          <Menu
            trigger={({ ...triggerProps }) => {
              return (
                <Pressable {...triggerProps}>
                  <Ionicons name="ellipsis-vertical" color="black" size={16} />
                </Pressable>
              );
            }}
          >
            <MenuItem
              onPress={() => setVisible(true)}
              key="Details"
              textValue={t("common:terms.details")}
            >
              <MenuItemLabel size="sm">
                {t("common:terms.details")}
              </MenuItemLabel>
            </MenuItem>
            <MenuItem
              onPress={() => setShowDeleteModal(true)}
              key="Delete"
              textValue={t("common:terms.delete")}
            >
              <MenuItemLabel size="sm">
                {t("common:terms.delete")}
              </MenuItemLabel>
            </MenuItem>
          </Menu>
        </View>
      </Pressable>
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
      >
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader>
            <Heading size="lg">{t("home/contacts:modal.delete.title")}</Heading>
            <ModalCloseButton>
              <AntDesign name="close" size={24} color="black" />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            <Text>{`${props.nickname} ${t(
              "home/contacts:modal.delete.body"
            )}`}</Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              size="sm"
              action="secondary"
              className="mr-2"
              onPress={() => {
                setShowDeleteModal(false);
              }}
            >
              <ButtonText>{t("common:terms.cancel")}</ButtonText>
            </Button>
            <Button
              variant="solid"
              size="sm"
              className="bg-purple1 mr-2"
              action="primary"
              onPress={() => {
                handleActionDelete();
              }}
            >
              <ButtonText className="text-white1">
                {t("common:terms.confirm")}
              </ButtonText>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <BeneficiaryModal
        visible={visible}
        data={props.data}
        onClose={() => setVisible(false)}
        onClick={(state) => setVisible(state)}
        onAction={() => setShowDeleteModal(true)}
      />
    </>
  );
}
